import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  WarningAmber as WarningAmberIcon
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { CounterpartyType } from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import {
  CounterpartyApprovalBlocker,
  getCounterpartyApprovalState
} from 'utils/helpers/counterPartyApproval';

import {
  approvalSuccessContainerStyle,
  approvalSuccessTextStyle,
  approvalSuccessTitleStyle,
  approvalWarningContainerStyle,
  approvalWarningHeadingStyle,
  approvalWarningRowStyle
} from 'styles/components/SlideOverStyle';

interface ApprovalPanelProps {
  readonly name: string;
  readonly type: CounterpartyType;
  readonly expenseTypeIds: number[];
  readonly ibans: { iban: string }[];
}

const BLOCKER_TRANSLATION_KEYS: Record<CounterpartyApprovalBlocker, string> = {
  name: 'addCounterparty.nameRequired',
  expenseType: 'addCounterparty.expenseTypeRequired',
  iban: 'addCounterparty.ibanRequired'
};

export const ApprovalPanel = ({
  name,
  type,
  expenseTypeIds,
  ibans
}: ApprovalPanelProps) => {
  const { translate } = useTranslations();

  const { isApproved, blockers } = getCounterpartyApprovalState({
    name,
    type,
    expenseTypeIds,
    ibans
  });

  if (isApproved) {
    return (
      <Box sx={approvalSuccessContainerStyle}>
        <CheckCircleOutlineIcon sx={{ color: '#2e7d32' }} />
        <Box>
          <Typography sx={approvalSuccessTitleStyle}>
            {translate('addCounterparty.approved')}
          </Typography>
          <Typography sx={approvalSuccessTextStyle}>
            {translate('addCounterparty.approvedMessage')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={approvalWarningContainerStyle}>
      <Typography sx={approvalWarningHeadingStyle}>
        {translate('addCounterparty.requiredBeforeApproval')}
      </Typography>
      {blockers.map((blocker) => (
        <WarningRow
          key={blocker}
          text={translate(BLOCKER_TRANSLATION_KEYS[blocker])}
        />
      ))}
    </Box>
  );
};

const WarningRow = ({ text }: { text: string }) => (
  <Box sx={approvalWarningRowStyle}>
    <WarningAmberIcon fontSize="inherit" />
    <Typography variant="inherit">{text}</Typography>
  </Box>
);
